import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceOS, EventName } from 'enums';
import { useDeepLink } from 'hooks/use-deeplink.hook';
import { TranslationNamespace } from 'i18n';
import { eventUtils, userDeviceUtils } from 'utils';

import {
  ROSA_ONE_DEEPLINKS_ANDROID,
  ROSA_ONE_DEEPLINKS_IOS,
} from '../../constants/rosa-one-deeplinks.constants';
import { ROSA_ONE_PALETTE } from '../../constants/rosa-one-palette.constant';
import { RosaOneButton } from '../RosaOneButton';
import type { MobileStep } from '../RosaOnePaymentDetails/RosaOnePaymentDetails';

const SberCheckmarkIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="18"
    height="18"
    viewBox="133 86 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M157.481 91.3813C158.13 92.2319 158.68 93.1561 159.119 94.1352L146.67 103.435L141.467 100.13V96.1538L146.67 99.459L157.481 91.3813Z"
      fill={color}
    />
    <path
      d="M136.157 99.8494C136.157 99.671 136.162 99.4938 136.17 99.3175L133.017 99.1609C133.006 99.3893 133 99.6199 133 99.8527C132.999 101.671 133.352 103.472 134.039 105.151C134.725 106.831 135.733 108.357 137.003 109.641L139.239 107.376C138.262 106.389 137.486 105.216 136.957 103.924C136.428 102.633 136.156 101.248 136.157 99.8494Z"
      fill={color}
    />
    <path
      d="M146.667 89.1993C146.843 89.1993 147.018 89.2058 147.192 89.2145L147.349 86.018C147.123 86.0071 146.896 86.0017 146.667 86.0017C144.872 85.9994 143.094 86.3566 141.436 87.0526C139.777 87.7486 138.271 88.7697 137.003 90.0574L139.239 92.324C140.213 91.3328 141.371 90.5465 142.645 90.0103C143.92 89.474 145.287 89.1984 146.667 89.1993Z"
      fill={color}
    />
    <path
      d="M146.667 110.501C146.491 110.501 146.316 110.501 146.141 110.487L145.983 113.682C146.21 113.694 146.438 113.7 146.667 113.7C148.461 113.701 150.238 113.344 151.896 112.648C153.553 111.952 155.059 110.93 156.326 109.643L154.093 107.377C153.119 108.368 151.962 109.154 150.688 109.69C149.413 110.226 148.047 110.502 146.667 110.501Z"
      fill={color}
    />
    <path
      d="M152.591 91.0575L155.248 89.0725C152.82 87.0794 149.788 85.9945 146.663 86V89.1987C148.779 89.1961 150.846 89.8442 152.591 91.0575Z"
      fill={color}
    />
    <path
      d="M160.333 99.8499C160.335 99.0178 160.263 98.1871 160.118 97.368L157.177 99.565C157.177 99.6596 157.177 99.7542 157.177 99.8499C157.177 101.338 156.87 102.81 156.273 104.17C155.677 105.53 154.805 106.747 153.715 107.744L155.836 110.12C157.254 108.823 158.387 107.239 159.162 105.469C159.936 103.7 160.335 101.786 160.333 99.8499Z"
      fill={color}
    />
    <path
      d="M146.667 110.501C145.198 110.502 143.746 110.19 142.404 109.585C141.062 108.981 139.861 108.098 138.877 106.993L136.533 109.142C137.813 110.579 139.376 111.727 141.122 112.512C142.868 113.298 144.757 113.702 146.667 113.7V110.501Z"
      fill={color}
    />
    <path
      d="M139.619 91.9557L137.499 89.5793C136.081 90.8761 134.947 92.4606 134.172 94.2299C133.397 95.9992 132.998 97.9138 133 99.8497H136.157C136.157 98.3616 136.465 96.8898 137.061 95.53C137.657 94.1701 138.529 92.9524 139.619 91.9557Z"
      fill={color}
    />
  </svg>
);

const QrScannerIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 36 36"
    focusable="false"
    fill={color}
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 23a1 1 0 0 1 .993.883L7 24v5h5a1 1 0 0 1 .117 1.993L12 31H7a2 2 0 0 1-1.995-1.85L5 29v-5a1 1 0 0 1 1-1Zm24 0a1 1 0 0 1 .993.883L31 24v5a2 2 0 0 1-1.85 1.994L29 31h-5a1 1 0 0 1-.117-1.993L24 29h5v-5a1 1 0 0 1 1-1Zm-5-2v4h-4v-4h4Zm-10 0v4h-4v-4h4Zm4 2v2h-2v-2h2Zm2-8v6h-6v-2h4v-2h-4v2h-2v-2h2v-2h6Zm4 2v2h-2v-2h2Zm-10-6v4h-4v-4h4Zm10 0v4h-4v-4h4Zm-6 0v2h-2v-2h2Zm5-6h5a2 2 0 0 1 1.994 1.85L31 7v5a1 1 0 0 1-1.993.117L29 12V7h-5a1 1 0 0 1-.117-1.993L24 5h5-5ZM7 5h5a1 1 0 0 1 .117 1.993L12 7H7v5a1 1 0 0 1-1.993.117L5 12V7a2 2 0 0 1 1.85-1.995L7 5h5-5Z"
    />
  </svg>
);

const PAY_DISABLE_DELAY_MS = 10_000;

const getSberDeeplinks = () => {
  const os = userDeviceUtils.getDeviceOS();
  if (os === DeviceOS.IOS) {
    return ROSA_ONE_DEEPLINKS_IOS;
  }
  if (os === DeviceOS.Android) {
    return ROSA_ONE_DEEPLINKS_ANDROID;
  }
  return '';
};

type RosaOneReadyPanelProps = {
  onStepChange?: (step: MobileStep) => void;
  confirmAction?: { label: string; onClick: () => void };
  confirmVisible?: boolean;
};

export const RosaOneReadyPanel: React.FC<RosaOneReadyPanelProps> = ({
  onStepChange,
  confirmAction,
  confirmVisible,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.rosa_one',
  });
  const { openDeepLinks } = useDeepLink();
  const [payDisabled, setPayDisabled] = useState(false);
  const [payClicked, setPayClicked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handlePayClick = useCallback(async () => {
    eventUtils.trackEvent({
      event: EventName.DeeplinkOpen,
    });
    onStepChange?.('gallery');
    setPayClicked(true);
    if (!payDisabled) {
      timerRef.current = setTimeout(
        () => setPayDisabled(true),
        PAY_DISABLE_DELAY_MS,
      );
    }
    const deeplinks = getSberDeeplinks();
    if (deeplinks) {
      await openDeepLinks(deeplinks);
    }
  }, [onStepChange, openDeepLinks, payDisabled]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div
      className="md:tw-hidden tw-px-4 tw-py-4 tw-border tw-border-solid tw-rounded-[32px]"
      style={{
        borderColor: 'rgba(55,235,160,.2)',
        background:
          'linear-gradient(145deg,rgba(22,43,61,.98),rgba(10,26,40,.99))',
      }}
    >
      <strong
        className="tw-block tw-mb-3 tw-text-[13px]"
        style={{ color: '#c9f8df' }}
      >
        {t('qr_saved_notice')}
      </strong>
      <RosaOneButton
        primary={!payDisabled}
        onClick={handlePayClick}
        className="tw-w-full tw-min-h-[50px] tw-rounded-[17px] tw-text-[15px]"
      >
        <SberCheckmarkIcon
          color={payDisabled ? ROSA_ONE_PALETTE.muted : ROSA_ONE_PALETTE.dark}
        />
        {t('pay')}
        <QrScannerIcon
          color={payDisabled ? ROSA_ONE_PALETTE.muted : ROSA_ONE_PALETTE.dark}
        />
      </RosaOneButton>
      <p
        className="tw-mt-[10px] tw-mb-0 tw-text-[11px] tw-text-center tw-leading-[1.5]"
        style={{ color: ROSA_ONE_PALETTE.muted }}
      >
        {t('gallery_hint')}
      </p>
      {payClicked && (
        <p
          className="tw-mt-[10px] tw-mb-0 tw-text-[11px] tw-text-center tw-leading-[1.5]"
          style={{ color: ROSA_ONE_PALETTE.muted }}
        >
          <strong style={{ color: '#f0c060' }}>
            {t('app_not_launched_warning')}
          </strong>{' '}
          {t('app_not_launched_hint')}
        </p>
      )}
      {confirmAction && confirmVisible && (
        <>
          <RosaOneButton
            primary={payDisabled}
            onClick={confirmAction.onClick}
            label={confirmAction.label}
            className="tw-w-full tw-mt-4 tw-px-6 tw-py-3 tw-rounded-[14px] tw-text-[14px]"
          />
          <p
            className="tw-mt-[10px] tw-mb-0 tw-text-[11px] tw-text-center tw-leading-[1.5]"
            style={{ color: ROSA_ONE_PALETTE.muted }}
          >
            {t('confirm_hint')}
          </p>
        </>
      )}
    </div>
  );
};
